import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";

/** Toasts for "saved"/"failed", and an in-app confirm dialog. The dialog
 *  replaces window.confirm, which cannot be styled and blocks the page. */
interface Toast { id: number; text: string; tone: "good" | "error" }
interface ConfirmReq { title: string; body?: ReactNode; confirm: string; danger?: boolean; resolve: (ok: boolean) => void }

const Ctx = createContext<{
  toast: (text: string, tone?: "good" | "error") => void;
  confirm: (opts: Omit<ConfirmReq, "resolve">) => Promise<boolean>;
} | null>(null);

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [req, setReq] = useState<ConfirmReq | null>(null);
  const seq = useRef(0);

  const toast = useCallback((text: string, tone: "good" | "error" = "good") => {
    const id = ++seq.current;
    setToasts((t) => [...t, { id, text, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), tone === "error" ? 7000 : 3500);
  }, []);

  const confirm = useCallback((opts: Omit<ConfirmReq, "resolve">) =>
    new Promise<boolean>((resolve) => setReq({ ...opts, resolve })), []);

  const close = (ok: boolean) => { req?.resolve(ok); setReq(null); };

  return (
    <Ctx.Provider value={{ toast, confirm }}>
      {children}
      <div className="toasts" aria-live="polite">
        {toasts.map((t) => <div key={t.id} className={`toast tone-${t.tone}`} role="status">{t.text}</div>)}
      </div>
      {req && (
        <div className="dialog-scrim" onClick={() => close(false)}>
          <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="dlg-title" onClick={(e) => e.stopPropagation()}>
            <h3 id="dlg-title">{req.title}</h3>
            {req.body && <div className="dialog-body">{req.body}</div>}
            <div className="dialog-actions">
              <button className="btn" onClick={() => close(false)}>Cancel</button>
              <button className={`btn ${req.danger ? "btn-danger" : "btn-primary"}`} autoFocus onClick={() => close(true)}>
                {req.confirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}

export function useFeedback() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useFeedback outside FeedbackProvider");
  return ctx;
}
